/**
 * `<SurfaceGate surface="...">` - renders its children only when the surface is
 * usable by this caller; otherwise renders NOTHING (ARCH-005-MOD-016): no
 * disabled control, no empty card, no tooltip explaining the absence. While the
 * answer is loading or has failed it also renders nothing (fail closed,
 * SPEC-TENANT-SURFACES RULE-014) - the scoped error and its Retry belong to the
 * shell layouts, never to each gated block.
 *
 * CODE-SPLIT (RULE-011): pass a `React.lazy` component as the child, inside a
 * `Suspense`, so an unusable block's code, strings and catalogue entries are not
 * in the initial bundle - a gate around an eagerly imported child hides the DOM
 * only, and the bundle still discloses the block:
 *
 *   const Block = React.lazy(() => import("./Block"));
 *   <SurfaceGate surface={blockKey}>
 *     <Suspense fallback={null}><Block /></Suspense>
 *   </SurfaceGate>
 *
 * @module surfaces/SurfaceGate
 * @see SPEC-TENANT-SURFACES-RULE-006 / RULE-011 / RULE-014
 * @see TASK-TSURF-W3-SURFACES-STORE-FE-CHG-003
 */
import React from "react";
import type { SurfaceKey } from "./keys";
import { useSurface } from "./useSurface";

export interface SurfaceGateProps {
  /** The registry key of the gated surface. */
  surface: SurfaceKey;
  /** Rendered only when the surface is usable; expected to be lazy (see the module header). */
  children: React.ReactNode;
}

export function SurfaceGate({ surface, children }: SurfaceGateProps): React.ReactElement | null {
  const { usable } = useSurface(surface);
  return usable ? <>{children}</> : null;
}

export default SurfaceGate;
